import { QueryClient, MutationCache } from "@tanstack/react-query";

import { queryKeys } from "@/shared/utils/queryKeys";
import type { Database } from "@/shared/types/database.types";
import type { EstateItemsResponse } from "@/shared/types/estateTypes";
import type { ItemBeneficiary } from "@/shared/hooks/useItemBeneficiaries";
import type { DocumentReference } from "@/shared/hooks/useItemDocumentReferences";
import type { ActivityLogEntry } from "@/shared/types/activityLog";
import type { InfiniteData } from "@tanstack/react-query";
import {
  DEMO_USER_ID,
  DEMO_MOCK_LAWYER_PERSONS,
  DEMO_ESTATE_ID,
  DEMO_ESTATE_ID_STR,
  DEMO_OWNER_USER_ID,
  DEMO_MOCK_ESTATE,
  DEMO_MOCK_ACCOUNT_OWNER,
  DEMO_MOCK_DOCUMENTS,
  DEMO_MOCK_CONTACTS,
  DEMO_MOCK_INVITATIONS,
  DEMO_MOCK_JOINED_USERS,
  DEMO_MOCK_ESTATE_ITEMS,
  DEMO_MOCK_ACTIVITY_LOG,
  DEMO_MOCK_OWNER_RELATIONSHIP,
  DEMO_MOCK_LAST_ACTIVITY,
  DEMO_MOCK_ITEM_BENEFICIARIES,
  DEMO_MOCK_ITEM_DOCUMENT_REFS,
  DEMO_MOCK_ITEM_ACTIVITY_LOGS,
  DEMO_ESTATE_ID_2,
  DEMO_ESTATE_ID_2_STR,
  DEMO_OWNER_USER_ID_2,
  DEMO_MOCK_ESTATE_2,
  DEMO_MOCK_ACCOUNT_OWNER_2,
  DEMO_MOCK_DOCUMENTS_2,
  DEMO_MOCK_CONTACTS_2,
  DEMO_MOCK_INVITATIONS_2,
  DEMO_MOCK_JOINED_USERS_2,
  DEMO_MOCK_ESTATE_ITEMS_2,
  DEMO_MOCK_ACTIVITY_LOG_2,
  DEMO_MOCK_OWNER_RELATIONSHIP_2,
  DEMO_MOCK_LAST_ACTIVITY_2,
  DEMO_MOCK_ITEM_BENEFICIARIES_2,
  DEMO_MOCK_ITEM_DOCUMENT_REFS_2,
  DEMO_MOCK_ITEM_ACTIVITY_LOGS_2,
  DEMO_ESTATE_ID_3,
  DEMO_ESTATE_ID_3_STR,
  DEMO_OWNER_USER_ID_3,
  DEMO_MOCK_ESTATE_3,
  DEMO_MOCK_ACCOUNT_OWNER_3,
  DEMO_MOCK_DOCUMENTS_3,
  DEMO_MOCK_CONTACTS_3,
  DEMO_MOCK_INVITATIONS_3,
  DEMO_MOCK_JOINED_USERS_3,
  DEMO_MOCK_ESTATE_ITEMS_3,
  DEMO_MOCK_ACTIVITY_LOG_3,
  DEMO_MOCK_OWNER_RELATIONSHIP_3,
  DEMO_MOCK_LAST_ACTIVITY_3,
  DEMO_MOCK_ITEM_BENEFICIARIES_3,
  DEMO_MOCK_ITEM_DOCUMENT_REFS_3,
  DEMO_MOCK_ITEM_ACTIVITY_LOGS_3,
} from "@/pages/demo/data/mockData";
import {
  DEMO_DOCUMENT_METADATA,
  DEMO_DOCUMENT_URLS,
  DEMO_DOCUMENT_METADATA_2,
  DEMO_DOCUMENT_URLS_2,
  DEMO_DOCUMENT_METADATA_3,
  DEMO_DOCUMENT_URLS_3,
} from "@/pages/demo/data/docExtractionData";

interface ActivityLogPage {
  entries: ActivityLogEntry[];
  nextCursor: { created_at: string; id: number } | null;
}

type EstateRow = Database["public"]["Tables"]["estates"]["Row"];

interface DemoEstateData {
  estateId: number;
  estateIdStr: string;
  ownerUserId: number;
  estate: EstateRow;
  accountOwner: { full_name: string; email: string; phone_number: string };
  documents: Record<string, unknown>[];
  contacts: Record<string, unknown>[];
  invitations: Record<string, unknown>[];
  joinedUsers: { user_id: number; email: string }[];
  estateItems: EstateItemsResponse;
  activityLog: InfiniteData<ActivityLogPage>;
  ownerRelationship: string;
  lastActivity: string;
  itemBeneficiaries: Record<number, ItemBeneficiary[]>;
  itemDocumentRefs: Record<number, DocumentReference[]>;
  itemActivityLogs: Record<number, InfiniteData<ActivityLogPage>>;
  documentMetadata?: Record<number, unknown>;
  documentUrls?: Record<number, string>;
}

export function createDemoQueryClient(): QueryClient {
  const client = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: Infinity,
        gcTime: Infinity,
        retry: false,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
      },
    },
    mutationCache: new MutationCache(),
  });

  seedDemoData(client);
  return client;
}

function seedEstate(client: QueryClient, data: DemoEstateData): void {
  const {
    estateId,
    estateIdStr,
    ownerUserId,
    estate,
    accountOwner,
    documents,
    contacts,
    invitations,
    joinedUsers,
    estateItems,
    activityLog,
    ownerRelationship,
    lastActivity,
    itemBeneficiaries,
    itemDocumentRefs,
    itemActivityLogs,
    documentMetadata,
    documentUrls,
  } = data;

  client.setQueryData(["lawyer-person-detail", estateIdStr], estate);
  client.setQueryData(["lawyer-person-account-holder", estateIdStr], accountOwner);
  client.setQueryData(["lawyer-person-documents", estateIdStr], documents);
  client.setQueryData(["lawyer-person-contacts", estateId], contacts);
  client.setQueryData(["lawyer-person-invitations", estateIdStr], invitations);
  client.setQueryData(["lawyer-user-persons", estateIdStr], joinedUsers);
  client.setQueryData(["lawyer-person-ssn", estateIdStr, false], null);
  client.setQueryData(queryKeys.estate.items(estateId), estateItems);
  client.setQueryData([...queryKeys.estate.activityLog(estateId), null], activityLog);
  client.setQueryData(["estate-last-activity", estateId], lastActivity);
  client.setQueryData(["lawyer-owner-relationship", ownerUserId, estateId], ownerRelationship);

  for (const [itemId, beneficiaries] of Object.entries(itemBeneficiaries)) {
    client.setQueryData(["item-beneficiaries", Number(itemId)], beneficiaries);
  }

  for (const [itemId, refs] of Object.entries(itemDocumentRefs)) {
    client.setQueryData(["item-document-references", Number(itemId), []], refs);
  }

  for (const [itemId, log] of Object.entries(itemActivityLogs)) {
    client.setQueryData([...queryKeys.estate.activityLog(estateId), Number(itemId)], log);
  }

  if (documentMetadata) {
    for (const [docId, metadata] of Object.entries(documentMetadata)) {
      client.setQueryData(["documentMetadata", Number(docId), estateId], metadata);
    }
  }

  if (documentUrls) {
    for (const [docId, url] of Object.entries(documentUrls)) {
      client.setQueryData(["documentUrl", Number(docId), estateId], url);
    }
  }
}

function seedDemoData(client: QueryClient): void {
  // Dashboard
  client.setQueryData(["lawyer-persons", DEMO_USER_ID], DEMO_MOCK_LAWYER_PERSONS);

  // Sidebar nav
  client.setQueryData(
    ["lawyer-estate-nav", DEMO_USER_ID],
    DEMO_MOCK_LAWYER_PERSONS.map((p) => ({
      id: p.estateId,
      estateId: p.estateId,
      decedentName: p.decedentName,
    })),
  );

  // Lawyer status
  client.setQueryData(["is-lawyer", DEMO_USER_ID], true);

  // Seed all three estates
  seedEstate(client, {
    estateId: DEMO_ESTATE_ID,
    estateIdStr: DEMO_ESTATE_ID_STR,
    ownerUserId: DEMO_OWNER_USER_ID,
    estate: DEMO_MOCK_ESTATE,
    accountOwner: DEMO_MOCK_ACCOUNT_OWNER,
    documents: DEMO_MOCK_DOCUMENTS,
    contacts: DEMO_MOCK_CONTACTS,
    invitations: DEMO_MOCK_INVITATIONS,
    joinedUsers: DEMO_MOCK_JOINED_USERS,
    estateItems: DEMO_MOCK_ESTATE_ITEMS,
    activityLog: DEMO_MOCK_ACTIVITY_LOG,
    ownerRelationship: DEMO_MOCK_OWNER_RELATIONSHIP,
    lastActivity: DEMO_MOCK_LAST_ACTIVITY,
    itemBeneficiaries: DEMO_MOCK_ITEM_BENEFICIARIES,
    itemDocumentRefs: DEMO_MOCK_ITEM_DOCUMENT_REFS,
    itemActivityLogs: DEMO_MOCK_ITEM_ACTIVITY_LOGS,
    documentMetadata: DEMO_DOCUMENT_METADATA,
    documentUrls: DEMO_DOCUMENT_URLS,
  });

  seedEstate(client, {
    estateId: DEMO_ESTATE_ID_2,
    estateIdStr: DEMO_ESTATE_ID_2_STR,
    ownerUserId: DEMO_OWNER_USER_ID_2,
    estate: DEMO_MOCK_ESTATE_2,
    accountOwner: DEMO_MOCK_ACCOUNT_OWNER_2,
    documents: DEMO_MOCK_DOCUMENTS_2,
    contacts: DEMO_MOCK_CONTACTS_2,
    invitations: DEMO_MOCK_INVITATIONS_2,
    joinedUsers: DEMO_MOCK_JOINED_USERS_2,
    estateItems: DEMO_MOCK_ESTATE_ITEMS_2,
    activityLog: DEMO_MOCK_ACTIVITY_LOG_2,
    ownerRelationship: DEMO_MOCK_OWNER_RELATIONSHIP_2,
    lastActivity: DEMO_MOCK_LAST_ACTIVITY_2,
    itemBeneficiaries: DEMO_MOCK_ITEM_BENEFICIARIES_2,
    itemDocumentRefs: DEMO_MOCK_ITEM_DOCUMENT_REFS_2,
    itemActivityLogs: DEMO_MOCK_ITEM_ACTIVITY_LOGS_2,
    documentMetadata: DEMO_DOCUMENT_METADATA_2,
    documentUrls: DEMO_DOCUMENT_URLS_2,
  });

  seedEstate(client, {
    estateId: DEMO_ESTATE_ID_3,
    estateIdStr: DEMO_ESTATE_ID_3_STR,
    ownerUserId: DEMO_OWNER_USER_ID_3,
    estate: DEMO_MOCK_ESTATE_3,
    accountOwner: DEMO_MOCK_ACCOUNT_OWNER_3,
    documents: DEMO_MOCK_DOCUMENTS_3,
    contacts: DEMO_MOCK_CONTACTS_3,
    invitations: DEMO_MOCK_INVITATIONS_3,
    joinedUsers: DEMO_MOCK_JOINED_USERS_3,
    estateItems: DEMO_MOCK_ESTATE_ITEMS_3,
    activityLog: DEMO_MOCK_ACTIVITY_LOG_3,
    ownerRelationship: DEMO_MOCK_OWNER_RELATIONSHIP_3,
    lastActivity: DEMO_MOCK_LAST_ACTIVITY_3,
    itemBeneficiaries: DEMO_MOCK_ITEM_BENEFICIARIES_3,
    itemDocumentRefs: DEMO_MOCK_ITEM_DOCUMENT_REFS_3,
    itemActivityLogs: DEMO_MOCK_ITEM_ACTIVITY_LOGS_3,
    documentMetadata: DEMO_DOCUMENT_METADATA_3,
    documentUrls: DEMO_DOCUMENT_URLS_3,
  });
}
